import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Calendar, Tag } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { Role } from '../../shared/types';

interface RoleDetailsDialogProps {
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (role: Role) => void;
  canEdit?: boolean;
}

export const RoleDetailsDialog: React.FC<RoleDetailsDialogProps> = ({
  role,
  open,
  onOpenChange,
  onEdit,
  canEdit = false,
}) => {
  const { t } = useTranslation();
  
  if (!role) return null;
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  const groupPermissions = (permissions: string[]) => {
    const groups: Record<string, string[]> = {};
    
    permissions.forEach(permission => {
      const [resource, action] = permission.split(':');
      if (!groups[resource]) {
        groups[resource] = [];
      }
      groups[resource].push(action || 'all');
    });
    
    return groups;
  };
  
  const permissionGroups = groupPermissions(role.permissions);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {t('roles.roleDetails')}
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit?.(role)}
              >
                {t('common.edit')}
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-6 pr-4">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('roles.basicInformation')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('common.name')}
                  </label>
                  <p className="text-lg font-semibold">{role.name}</p>
                </div>
                
                {role.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('common.description')}
                    </label>
                    <p className="text-sm">{role.description}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('roles.serviceName')}
                  </label>
                  <div className="mt-1">
                    {role.serviceName ? (
                      <Badge variant="secondary" className="text-sm">
                        <Tag className="mr-1 h-3 w-3" />
                        {role.serviceName}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-sm">
                        Global Role
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('common.createdAt')}
                    </label>
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(role.createdAt)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('common.updatedAt')}
                    </label>
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(role.updatedAt)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Permissions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  {t('roles.permissions')}
                  <Badge variant="outline">
                    {role.permissions.length} {t('roles.permissions').toLowerCase()}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {role.permissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('roles.noPermissions')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(permissionGroups).map(([resource, actions]) => (
                      <div key={resource} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium capitalize">{resource}</h4>
                          <div className="flex-1 border-t border-border" />
                        </div>
                        <div className="flex flex-wrap gap-2 ml-4">
                          {actions.map((action) => (
                            <Badge
                              key={`${resource}:${action}`}
                              variant="secondary"
                              className="text-xs"
                            >
                              {action}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Raw Permissions List */}
            {role.permissions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('roles.rawPermissions')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 rounded-md p-4">
                    <code className="text-sm">
                      <div className="space-y-1">
                        {role.permissions.map((permission, index) => (
                          <div key={permission} className="flex items-center gap-2">
                            <span className="text-muted-foreground text-xs w-6">
                              {index + 1}.
                            </span>
                            <span className="font-mono">{permission}</span>
                          </div>
                        ))}
                      </div>
                    </code>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};